import type { Chiffre } from './chiffrement.js';
import { GARDE_INITIALE, memeGarde, type GardeStockage, type MagasinHorsLigne } from './magasin.js';

export const NOM_BASE_HORS_LIGNE = 'artosera-hors-ligne';
const VERSION_BASE = 2;
const CONTROLE = 'controle';
const CANAL = 'artosera-hors-ligne-invalidation';
const CLES = 'cles';
const INSTANTANES = 'instantanes';

function promesse<T>(requete: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    requete.onsuccess = () => resoudre(requete.result);
    requete.onerror = () => rejeter(requete.error ?? new Error('IndexedDB a refusé la requête.'));
  });
}

function ouvrir(fabrique: IDBFactory): Promise<IDBDatabase> {
  const requete = fabrique.open(NOM_BASE_HORS_LIGNE, VERSION_BASE);
  requete.onupgradeneeded = () => {
    const base = requete.result;
    if (!base.objectStoreNames.contains(CONTROLE)) base.createObjectStore(CONTROLE);
    if (!base.objectStoreNames.contains(CLES)) base.createObjectStore(CLES);
    if (!base.objectStoreNames.contains(INSTANTANES)) base.createObjectStore(INSTANTANES);
  };
  return promesse(requete).then((base) => {
    base.onversionchange = () => base.close();
    return base;
  });
}

async function transaction<T>(
  fabrique: IDBFactory,
  magasins: string[],
  mode: IDBTransactionMode,
  faire: (tx: IDBTransaction) => Promise<T>,
): Promise<T> {
  const base = await ouvrir(fabrique);
  try {
    const tx = base.transaction(magasins, mode);
    const fin = new Promise<void>((resoudre, rejeter) => {
      tx.oncomplete = () => resoudre();
      tx.onerror = () => rejeter(tx.error ?? new Error('Transaction IndexedDB abandonnée.'));
      tx.onabort = () => rejeter(tx.error ?? new Error('Transaction IndexedDB abandonnée.'));
    });
    const [resultat] = await Promise.all([faire(tx), fin]);
    return resultat;
  } finally {
    base.close();
  }
}

async function lireGarde(tx: IDBTransaction): Promise<GardeStockage> {
  return (
    ((await promesse(tx.objectStore(CONTROLE).get('courante'))) as GardeStockage | undefined) ??
    GARDE_INITIALE
  );
}
async function effacer(tx: IDBTransaction): Promise<void> {
  // La clé est retirée avant le chiffré dans la même transaction atomique.
  await promesse(tx.objectStore(CLES).clear());
  await promesse(tx.objectStore(INSTANTANES).clear());
}
function annoncer(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const canal = new BroadcastChannel(CANAL);
  canal.postMessage('verification');
  canal.close();
}

/** Toutes les décisions de propriétaire et publications se sérialisent dans IndexedDB. */
export function magasinIndexedDb(fabrique: IDBFactory = indexedDB): MagasinHorsLigne {
  const magasins = [CONTROLE, CLES, INSTANTANES];
  async function invalider(id?: string): Promise<void> {
    await transaction(fabrique, magasins, 'readwrite', async (tx) => {
      const ancienne = await lireGarde(tx);
      if (id && ancienne.proprietaire && ancienne.proprietaire !== id) return;
      await effacer(tx);
      await promesse(
        tx
          .objectStore(CONTROLE)
          .put({ ...GARDE_INITIALE, generation: ancienne.generation + 1 }, 'courante'),
      );
    });
    annoncer();
  }
  return {
    controle: () => transaction(fabrique, [CONTROLE], 'readonly', lireGarde),
    autoriser: async (attendue, proprietaire, autorisation, forcer) => {
      const resultat = await transaction(fabrique, magasins, 'readwrite', async (tx) => {
        const ancienne = await lireGarde(tx);
        if (ancienne.generation !== attendue) return null;
        if (
          !forcer &&
          ancienne.proprietaire === proprietaire &&
          ancienne.autorisation === autorisation
        )
          return ancienne;
        const suivante = { generation: ancienne.generation + 1, proprietaire, autorisation };
        await effacer(tx);
        await promesse(tx.objectStore(CONTROLE).put(suivante, 'courante'));
        return suivante;
      });
      annoncer();
      return resultat;
    },
    invalider: () => invalider(),
    publier: (garde, cle, chiffre) =>
      transaction(fabrique, magasins, 'readwrite', async (tx) => {
        if (!garde.proprietaire || !memeGarde(await lireGarde(tx), garde)) return false;
        await effacer(tx);
        await promesse(tx.objectStore(CLES).put(cle, garde.proprietaire));
        await promesse(tx.objectStore(INSTANTANES).put(chiffre, garde.proprietaire));
        return true;
      }),
    surveiller: (rappel) => {
      const canal = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CANAL);
      if (canal) canal.onmessage = rappel;
      // Reprise d'un onglet suspendu et repli sans BroadcastChannel : la source
      // d'autorité reste toujours le contrôle durable, jamais le message.
      const minuterie = setInterval(rappel, 1000);
      window.addEventListener('focus', rappel);
      window.addEventListener('pageshow', rappel);
      document.addEventListener('visibilitychange', rappel);
      return () => {
        canal?.close();
        clearInterval(minuterie);
        window.removeEventListener('focus', rappel);
        window.removeEventListener('pageshow', rappel);
        document.removeEventListener('visibilitychange', rappel);
      };
    },
    lireCle: (id) =>
      transaction(
        fabrique,
        [CLES],
        'readonly',
        async (tx) =>
          ((await promesse(tx.objectStore(CLES).get(id))) as CryptoKey | undefined) ?? null,
      ),
    ecrireCle: (id, cle) =>
      transaction(fabrique, [CLES], 'readwrite', async (tx) => {
        await promesse(tx.objectStore(CLES).put(cle, id));
      }),
    lireInstantane: (id) =>
      transaction(
        fabrique,
        [INSTANTANES],
        'readonly',
        async (tx) =>
          ((await promesse(tx.objectStore(INSTANTANES).get(id))) as Chiffre | undefined) ?? null,
      ),
    ecrireInstantane: (id, chiffre) =>
      transaction(fabrique, [INSTANTANES], 'readwrite', async (tx) => {
        await promesse(tx.objectStore(INSTANTANES).put(chiffre, id));
      }),
    supprimer: (id) => invalider(id),
    identifiants: () =>
      transaction(fabrique, [CLES, INSTANTANES], 'readonly', async (tx) => {
        const [cles, instantanes] = await Promise.all([
          promesse(tx.objectStore(CLES).getAllKeys()),
          promesse(tx.objectStore(INSTANTANES).getAllKeys()),
        ]);
        return [...new Set([...cles, ...instantanes].map(String))].sort();
      }),
  };
}
